import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Globe, Users, FileText, CheckCircle2, XCircle, DollarSign, TrendingUp, UserCheck, Briefcase, AlertCircle, BarChart3 } from 'lucide-react';
import { getCommissionRecords, getWhiteLabelDashboard } from '../../lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';

const formatCurrency = (value) => {
  if (!value) return '₹0';
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
};

const StatCard = ({ label, value, icon: Icon, color, bg }) => (
  <div className={`rounded-xl border p-5 flex items-center gap-4 shadow-sm ${bg}`}>
    <div className={`p-3 rounded-lg ${color}`}>
      <Icon className="h-6 w-6 text-white" />
    </div>
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  </div>
);

const WhiteLabelDashboard = () => {
  const { user, token } = useAuth();
  const displayName = user?.full_name || user?.name || 'User';
  const brandName = user?.workspace?.brand_name || user?.brand_name || user?.brandName || 'White Label';
  const [data, setData] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!token) return;
      setLoading(true);
      try {
        const [dashboardResp, recordsResp] = await Promise.all([
          getWhiteLabelDashboard(token),
          getCommissionRecords(token),
        ]);
        if (active) {
          setData(dashboardResp?.data || null);
          setRecords(Array.isArray(recordsResp?.data?.items) ? recordsResp.data.items.slice(0, 5) : []);
        }
      } catch {
        if (active) {
          setData(null);
          setRecords([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [token]);

  const stats = [
    { label: 'Total Leads', value: data?.totalLeads || 0, icon: Users, color: 'bg-purple-500', bg: '' },
    { label: 'Total File Login', value: data?.totalFileLogin || 0, icon: FileText, color: 'bg-blue-500', bg: '' },
    { label: 'Under Processing', value: data?.underProcessing || 0, icon: TrendingUp, color: 'bg-amber-500', bg: '' },
    { label: 'Approved Loans', value: data?.approvedLoans || 0, icon: CheckCircle2, color: 'bg-emerald-500', bg: '' },
    { label: 'Rejected Files', value: data?.rejectedLoans || 0, icon: XCircle, color: 'bg-red-500', bg: '' },
    { label: 'Disbursement Amt', value: formatCurrency(data?.disbursementAmount || 0), icon: DollarSign, color: 'bg-indigo-500', bg: '' },
    { label: 'My Commission', value: formatCurrency(data?.ownCommission || 0), icon: UserCheck, color: 'bg-pink-500', bg: '' },
    { label: 'Workspace Commission', value: formatCurrency(data?.workspaceCommission || 0), icon: BarChart3, color: 'bg-teal-500', bg: '' },
    { label: 'Total Agents', value: data?.totalAgents || 0, icon: UserCheck, color: 'bg-orange-500', bg: '' },
    { label: 'Total Employees', value: data?.totalEmployees || 0, icon: Briefcase, color: 'bg-cyan-500', bg: '' },
  ];

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const chipClass = (status) => (
    status === 'paid'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'approved'
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-amber-50 text-amber-700 border-amber-200'
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
          <Globe className="h-6 w-6 text-purple-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">{brandName} Dashboard</h1>
          <p className="text-sm text-muted-foreground">Welcome back, {displayName} — manage your branded portal</p>
        </div>
        <div className="ml-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border border-purple-200 dark:border-purple-800">
            <Globe className="h-3 w-3" />
            White Label Admin
          </span>
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border p-8 text-center text-muted-foreground">Loading dashboard...</div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {stats.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border p-5 shadow-sm">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-purple-500" />
            Loan Stage Pipeline
          </h2>
          <div className="space-y-3">
            {(data?.pipeline || []).map((item) => {
              const pct = data?.totalFileLogin ? Math.round((item.count / data.totalFileLogin) * 100) : 0;
              return (
                <div key={item.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className="font-medium">{item.count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full bg-purple-500 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border p-5 shadow-sm">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-purple-500" />
            Top Agent Performance
          </h2>
          <div className="space-y-3">
            {(data?.topAgents || []).map((agent, i) => (
              <div key={agent.name} className="flex items-center gap-3 py-2 border-b last:border-0">
                <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-xs font-bold text-purple-700 dark:text-purple-400">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{agent.name}</p>
                  <p className="text-xs text-muted-foreground">{agent.leads} leads · {agent.files} files</p>
                </div>
                <span className="text-sm font-semibold text-emerald-600">{formatCurrency(agent.commission)}</span>
              </div>
            ))}
            {data && (!data.topAgents || data.topAgents.length === 0) ? (
              <p className="text-sm text-muted-foreground">No agent commission data yet.</p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="rounded-xl border p-5 shadow-sm">
        <h2 className="font-semibold mb-4 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          Recent Commission Entries
        </h2>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Loan</TableHead>
              <TableHead>Recipient</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-mono text-sm">{row.loan_application_id}</TableCell>
                <TableCell>{row.recipient_name || row.recipient_role}</TableCell>
                <TableCell className="font-medium">{formatCurrency(Number(row.amount || 0))}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={chipClass(row.status)}>
                    {row.status}
                  </Badge>
                </TableCell>
                <TableCell>{formatDate(row.paid_at || row.created_at)}</TableCell>
              </TableRow>
            ))}
            {!loading && records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  No commission entries found.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default WhiteLabelDashboard;
